import { useState } from "react";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import {
  ConstructionSite,
  BuildingMaterialComponent,
  MountingAccessories,
  SingleSectionElement,
} from "features/addingMeasurement";
import { Button, Input } from "components";
import { VALIDATION_MESSAGES } from "enums";
import { usePutInstallationInfo } from "hooks/measurements";
import { useSearchParams } from "react-router-dom";
import { ScrollToError } from "utils";

interface WindowsInRowFormInstallationInformationType {
  measurementUuid: string | null;
  measurementInfo: any;
  step: number;
  setStep(step: number): void;
  prevStep: () => void;
}

const WindowsInRowFormInstallationInformation = ({
  measurementUuid,
  measurementInfo,
  setStep,
  step,
  prevStep,
}: WindowsInRowFormInstallationInformationType) => {
  const [isValidated, setIsValidated] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialMeasurementInstallationInfo =
    measurementInfo?.measurementInstallationInfo;

  const { mutate: putInstallationInfo } = usePutInstallationInfo({
    onSuccess: () => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("measurementPage", "4");
      setSearchParams(newSearchParams, { replace: true });
      setStep(step + 1);
    },
    onError: () => {
      console.error("Błąd podczas aktualizacji danych klienta.");
    },
  });

  interface ObjectWithComment {
    comment: string;
  }

  const isObjectWithComment = (value: any): value is ObjectWithComment => {
    return (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      typeof value.comment === "string" &&
      value.comment.trim() !== ""
    );
  };

  const validationSchema = Yup.object().shape({
    constructionSiteUuids: Yup.array()
      .of(
        Yup.mixed().test(
          "is-string-or-object-with-comment",
          VALIDATION_MESSAGES.REQUIRED,
          (value) =>
            typeof value === "string" ||
            (typeof value === "object" &&
              value !== null &&
              !Array.isArray(value) &&
              typeof (value as ObjectWithComment).comment === "string" &&
              (value as ObjectWithComment).comment.trim() !== "")
        )
      )
      .required(VALIDATION_MESSAGES.REQUIRED)
      .test(
        "has-at-least-one-loose-string-or-object-with-comment",
        VALIDATION_MESSAGES.REQUIRED,
        (array) =>
          array.some(
            (item) => typeof item === "string" || isObjectWithComment(item)
          )
      ),
    theBuildingIsMadeOf: Yup.string()
      .required(VALIDATION_MESSAGES.REQUIRED)
      .test("is-other-check", VALIDATION_MESSAGES.REQUIRED, function (value) {
        const { theBuildingIsMadeOfComment } = this.parent;
        if (value === "other" && !theBuildingIsMadeOfComment) {
          return false;
        }
        return true;
      }),
    theBuildingIsMadeOfComment: Yup.string(),
    plannedThickness: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    plannedAssemblyTime: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
    numberOfAssemblyTeams: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  });

  const handleOnSubmit = (values: any) => {
    if (values.theBuildingIsMadeOf !== "other") {
      delete values.theBuildingIsMadeOfComment;
    }
    delete values.constructionSiteComments;
    putInstallationInfo(values);
  };

  const constructionSiteComments =
    initialMeasurementInstallationInfo?.constructionSiteUuids.find(
      (item: any) => item.comment
    )?.comment;

  return (
    <div>
      <div className="am-heading">Informacje montażowe</div>
      <Formik
        initialValues={{
          measurementUuid: measurementUuid,
          constructionSiteUuids:
            initialMeasurementInstallationInfo?.constructionSiteUuids || [],
          theBuildingIsMadeOfComment:
            initialMeasurementInstallationInfo?.theBuildingIsMadeOfComment ||
            "",
          constructionSiteComments: constructionSiteComments || "",
          theBuildingIsMadeOf:
            initialMeasurementInstallationInfo?.theBuildingIsMadeOf || "",
          plannedThickness:
            initialMeasurementInstallationInfo?.plannedThickness || "",
          numberOfAssemblyTeams:
            initialMeasurementInstallationInfo?.numberOfAssemblyTeams || "",
          plannedAssemblyTime:
            initialMeasurementInstallationInfo?.plannedAssemblyTime || "",
          installationElementsUuids:
            initialMeasurementInstallationInfo?.installationElementsUuids || [],
        }}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={isValidated}
        onSubmit={async (values) => handleOnSubmit(values)}
      >
        {({ handleSubmit, errors, values, submitCount, touched }) => {
          if (submitCount > 0) {
            setIsValidated(true);
          }
          return (
            <form className="amfs__form form" onSubmit={handleSubmit}>
              <ScrollToError />
              <Field
                type={measurementInfo?.typeOfMeasurement}
                values={values}
                errors={errors}
                name="constructionSiteUuids"
                component={ConstructionSite}
              />
              <Field
                errors={errors}
                name="theBuildingIsMadeOf"
                component={BuildingMaterialComponent}
              />
              <SingleSectionElement
                touched={touched}
                name="plannedThickness"
                errors={errors}
              >
                <Field
                  label="Planowana grubość ocieplenia"
                  name="plannedThickness"
                  type="number"
                  rightPlaceholder="mm"
                  component={Input}
                />
              </SingleSectionElement>
              <Field
                optional
                type={measurementInfo?.typeOfMeasurement}
                errors={errors}
                name="installationElementsUuids"
                component={MountingAccessories}
              />
              <SingleSectionElement
                touched={touched}
                name="plannedAssemblyTime"
                errors={errors}
              >
                <Field
                  label="Planowany czas montażu (w godzinach)"
                  name="plannedAssemblyTime"
                  maxLength={6}
                  type="number"
                  rightPlaceholder="h"
                  component={Input}
                />
              </SingleSectionElement>
              <SingleSectionElement
                className="single-section-element--margin-top"
                touched={touched}
                name="numberOfAssemblyTeams"
                errors={errors}
              >
                <Field
                  label="Ilość osób potrzebnych do montażu"
                  name="numberOfAssemblyTeams"
                  type="number"
                  component={Input}
                  maxLength={3}
                />
              </SingleSectionElement>
              <div className="am__buttons">
                <Button
                  stroke
                  onClick={() => prevStep()}
                  label="Poprzedni krok"
                />
                <Button onClick={() => handleSubmit()} label="Dalej" />
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default WindowsInRowFormInstallationInformation;
